import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import {
  CatalogOffering, CatalogService, CatalogWarehouse, CatalogWarehouseBin, InventoryLot, InventoryMovement, InventoryMovementPayload
} from './catalog.service';
import { InventoryCostingApiService } from './inventory-costing-api.service';
import { inventoryOperationTypeLabel, inventoryOperationTypeOptions, isTransferOperationType } from './inventory-movement-labels';

interface OptionItem {
  label: string;
  value: string;
}

@Component({
  standalone: true,
  selector: 'ain-inventory-movement-operations-panel',
  imports: [
    AinStateComponent, InfoHintComponent, FormsModule, ButtonModule, InputTextModule, SelectModule, TableModule,
    DialogModule, AinCanAccessDirective, CurrencySelectorComponent
  ],
  template: `
    @if (loading()) {
      <ain-state type="loading" [title]="t('catalog.loading')" />
    } @else {
      @if (fixedOfferingId) {
        <ain-info-hint
          [titleKey]="'catalog.offerings.inventory.movements.hint.title'"
          [descriptionKey]="'catalog.offerings.inventory.movements.hint.description'" />
      }

      <div class="toolbar">
        <button pButton type="button" [label]="t('catalog.movements.operationTitle')" (click)="openOperationDialog()"></button>
        <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
      </div>

      @if (drafts().length > 0) {
        <section class="list-card">
          <h3>{{ t('catalog.movements.draftsTitle') }}</h3>
          @for (d of drafts(); track d.id) {
            <div class="draft-row">
              <span>{{ operationTypeLabel(d.operationType) }} · {{ d.referenceNumber }} · {{ d.status }}</span>
              <div class="draft-actions">
                <button pButton type="button" size="small" severity="secondary" [label]="t('common.view')" (click)="openDetail(d.id)"></button>
                <button pButton type="button" size="small" [label]="t('catalog.movements.approveDraft')" (click)="approveDraft(d.id)"></button>
              </div>
            </div>
          }
        </section>
      }

      <section class="list-card">
        <h3>{{ t('catalog.movements.historyTitle') }}</h3>
        <p-table [value]="movements()" [paginator]="true" [rows]="15">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('catalog.inventory.operation') }}</th>
              <th>{{ t('catalog.inventory.reference') }}</th>
              @if (!fixedOfferingId) {
                <th>{{ t('catalog.inventory.offeringId') }}</th>
              }
              <th>{{ t('catalog.inventory.quantity') }}</th>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('catalog.costing.gl.status') }}</th>
              <th>{{ t('catalog.inventory.occurredAt') }}</th>
              <th>{{ t('columns.actions') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row.id)">{{ operationTypeLabel(row.operationType) }}</button>
              </td>
              <td>{{ row.referenceNumber || '-' }}</td>
              @if (!fixedOfferingId) {
                <td>{{ resolveOfferingLabel(row.lines?.[0]?.offeringId) }}</td>
              }
              <td>{{ lineQuantity(row) }}</td>
              <td>{{ row.status }}</td>
              <td>{{ row.glPostingStatus || '-' }}</td>
              <td>{{ row.processedAtUtc }}</td>
              <td>
                @if (isTransferOperationType(row.operationType)) {
                  <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.movements.printTransfer')" (click)="printTransfer(row.id)"></button>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      </section>

      @if (error()) {
        <p class="error">{{ error() }}</p>
      }
    }

    <p-dialog
      [(visible)]="operationDialogVisible"
      [header]="t('catalog.movements.operationTitle')"
      [modal]="true"
      [style]="{ width: 'min(720px, 96vw)' }"
      (onHide)="closeOperationDialog()">
      <div class="form-grid">
        <label>
          <span>{{ t('catalog.movements.operationType') }}</span>
          <p-select [(ngModel)]="form.operationType" [options]="operationTypeOptions()" optionLabel="label" optionValue="value" appendTo="body" />
        </label>
        @if (!fixedOfferingId) {
          <label>
            <span>{{ t('catalog.inventory.offeringId') }}</span>
            <p-select [(ngModel)]="form.offeringId" [options]="offeringOptions()" optionLabel="label" optionValue="value" appendTo="body" [filter]="true" filterBy="label" />
          </label>
        }
        @if (form.operationType === 'issue' || form.operationType === 'receipt') {
          <label>
            <span>{{ t('catalog.tracking.lotId') }}</span>
            <p-select [(ngModel)]="form.lotId" [options]="lotOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" />
          </label>
          <div class="actions">
            <button pButton type="button" severity="secondary" [label]="t('catalog.operator.suggestLots')" (click)="suggestLots()"></button>
          </div>
          @if (lotSuggestions().length > 0) {
            <p class="hint">{{ lotSuggestions().map(s => s.lotNumber + ':' + s.suggestedQuantity).join(', ') }}</p>
          }
        }
        <label>
          <span>{{ t('catalog.inventory.quantity') }}</span>
          <input pInputText type="number" min="0" step="0.001" [(ngModel)]="form.quantity" />
        </label>
        @if (requiresUnitCost()) {
          <label>
            <span>{{ t('catalog.costing.layers.unitCost') }}</span>
            <input pInputText type="number" min="0" step="0.0001" [(ngModel)]="form.unitCost" />
          </label>
          <label>
            <span>{{ t('columns.currency') }}</span>
            <ain-currency-selector [(ngModel)]="form.currencyCode" />
          </label>
          <p class="hint cost-hint">{{ t('catalog.movements.unitCostHint') }}</p>
        }
        <label>
          <span>{{ t('catalog.inventory.reference') }}</span>
          <input pInputText [(ngModel)]="form.referenceNumber" />
        </label>

        @if (isTransfer()) {
          <label>
            <span>{{ t('catalog.movements.fromWarehouse') }}</span>
            <p-select [(ngModel)]="form.fromWarehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
          </label>
          <label>
            <span>{{ t('catalog.movements.fromBin') }}</span>
            <p-select [(ngModel)]="form.fromBinId" [options]="binOptions(form.fromWarehouseId)" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
          </label>
          <label>
            <span>{{ t('catalog.movements.toWarehouse') }}</span>
            <p-select [(ngModel)]="form.toWarehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
          </label>
          <label>
            <span>{{ t('catalog.movements.toBin') }}</span>
            <p-select [(ngModel)]="form.toBinId" [options]="binOptions(form.toWarehouseId)" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
          </label>
        } @else {
          <label>
            <span>{{ t('catalog.inventory.warehouseId') }}</span>
            <p-select [(ngModel)]="form.warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" (ngModelChange)="refreshCostingDefaults()" />
          </label>
          <label>
            <span>{{ t('catalog.movements.bin') }}</span>
            <p-select [(ngModel)]="form.binId" [options]="binOptions(form.warehouseId)" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
          </label>
        }

        <label>
          <span>{{ t('catalog.movements.idempotencyKey') }}</span>
          <div class="inline-actions">
            <input pInputText [(ngModel)]="form.idempotencyKey" />
            <button pButton type="button" severity="secondary" [label]="t('catalog.movements.generateKey')" (click)="generateIdempotencyKey()"></button>
          </div>
        </label>
      </div>
      <label class="check-row">
        <input type="checkbox" [(ngModel)]="form.applyLotRotation" />
        <span>{{ t('catalog.movements.applyLotRotation') }}</span>
      </label>
      @if (form.operationType === 'issue' || form.operationType === 'internal-consumption') {
        <label>
          <span>{{ t('catalog.movements.fefoOverrideReason') }}</span>
          <input pInputText [(ngModel)]="form.fefoOverrideReason" />
        </label>
      }
      <label class="check-row">
        <input type="checkbox" [(ngModel)]="form.submitAsDraft" />
        <span>{{ t('catalog.movements.submitAsDraft') }}</span>
      </label>
      @if (operationError()) {
        <p class="error">{{ operationError() }}</p>
      }
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeOperationDialog()"></button>
        <button pButton type="button" [label]="t('common.save')" (click)="submitOperation()"></button>
      </ng-template>
    </p-dialog>

    <p-dialog
      [(visible)]="detailDialogVisible"
      [header]="t('catalog.movements.detailTitle')"
      [modal]="true"
      [style]="{ width: 'min(720px, 96vw)' }"
      (onHide)="closeDetail()">
      @if (detailLoading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (detailMovement(); as movement) {
        <div class="detail-summary">
          <p><strong>{{ operationTypeLabel(movement.operationType) }}</strong> · {{ movement.referenceNumber || '-' }} · {{ movement.status }}</p>
          <p>{{ t('catalog.movements.idempotencyKey') }}: {{ movement.idempotencyKey }}</p>
          <p>{{ t('catalog.costing.gl.status') }}: {{ movement.glPostingStatus || '-' }}</p>
          @if (canRetryGl(movement)) {
            <button pButton type="button" size="small" ainCanAccess [ainCanAccessPermission]="'inventory.movements.post-gl'" [label]="t('catalog.costing.gl.retry')" [disabled]="retryingGl()" (click)="retryGl(movement.id)"></button>
          }
          @if (isTransferOperationType(movement.operationType)) {
            <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.movements.printTransfer')" (click)="printTransfer(movement.id)"></button>
          }
        </div>
        <p-table [value]="movement.lines">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('catalog.inventory.warehouseId') }}</th>
              <th>{{ t('catalog.inventory.quantity') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-line>
            <tr>
              <td>{{ resolveWarehouseLabel(line.fromWarehouseId || line.toWarehouseId) }}</td>
              <td>{{ line.quantity }}</td>
            </tr>
          </ng-template>
        </p-table>
      } @else if (detailError()) {
        <p class="error">{{ detailError() }}</p>
      }
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
    .list-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 12px; background: #fff; }
    .list-card h3 { margin: 0 0 10px; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid span { color: #475569; font-size: 12px; font-weight: 600; }
    .inline-actions { display: flex; gap: 8px; }
    .actions { margin-top: 10px; display: flex; gap: 8px; flex-wrap: wrap; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { margin: 10px 0 0; color: #b91c1c; }
    .hint { color: #475569; font-size: 12px; grid-column: 1 / -1; }
    .check-row { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
    .draft-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #f1f5f9; gap: 8px; flex-wrap: wrap; }
    .draft-actions { display: flex; gap: 6px; }
    .detail-summary { display: grid; gap: 6px; margin-bottom: 12px; }
    .detail-summary p { margin: 0; font-size: 14px; }
  `]
})
export class InventoryMovementOperationsPanelComponent implements OnInit {
  @Input() fixedOfferingId: string | null = null;
  @Output() inventoryChanged = new EventEmitter<void>();

  private readonly service = inject(CatalogService);
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly movements = signal<InventoryMovement[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly bins = signal<CatalogWarehouseBin[]>([]);
  readonly lots = signal<InventoryLot[]>([]);
  readonly lotSuggestions = signal<{ lotNumber: string; suggestedQuantity: number }[]>([]);
  readonly drafts = signal<InventoryMovement[]>([]);

  operationDialogVisible = false;
  readonly operationError = signal('');
  detailDialogVisible = false;
  readonly detailLoading = signal(false);
  readonly detailMovement = signal<InventoryMovement | null>(null);
  readonly detailError = signal('');
  readonly retryingGl = signal(false);

  readonly operationTypeOptions = () => inventoryOperationTypeOptions(key => this.t(key));
  operationTypeLabel = (operationType: string) => inventoryOperationTypeLabel(operationType, key => this.t(key));
  readonly isTransferOperationType = isTransferOperationType;

  readonly form = {
    operationType: 'receipt',
    offeringId: '',
    quantity: 1,
    warehouseId: '',
    binId: '',
    fromWarehouseId: '',
    fromBinId: '',
    toWarehouseId: '',
    toBinId: '',
    idempotencyKey: '',
    referenceNumber: '',
    lotId: '',
    applyLotRotation: false,
    fefoOverrideReason: '',
    submitAsDraft: false,
    unitCost: 0,
    currencyCode: 'IRR'
  };

  ngOnInit(): void {
    if (this.fixedOfferingId) {
      this.form.offeringId = this.fixedOfferingId;
    }
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.generateIdempotencyKey();
    this.loadLots();
    this.load();
  }

  private loadLots(): void {
    const offeringId = this.fixedOfferingId ?? undefined;
    this.service.listInventoryLots(offeringId).subscribe({ next: lots => this.lots.set(lots) });
  }

  private matchesOffering(movement: InventoryMovement): boolean {
    if (!this.fixedOfferingId) {
      return true;
    }
    return movement.lines?.some(line => line.offeringId === this.fixedOfferingId) ?? false;
  }

  lineQuantity(row: InventoryMovement): number {
    const lines = this.fixedOfferingId
      ? row.lines?.filter(line => line.offeringId === this.fixedOfferingId) ?? []
      : row.lines ?? [];
    return lines.reduce((sum, line) => sum + line.quantity, 0);
  }

  suggestLots(): void {
    const offeringId = this.fixedOfferingId ?? this.form.offeringId;
    if (!offeringId || !this.form.warehouseId || this.form.quantity <= 0) return;
    this.service.getInventoryLotSuggestions(
      offeringId,
      this.form.warehouseId,
      Number(this.form.quantity),
      this.form.binId || null
    ).subscribe({
      next: rows => {
        this.lotSuggestions.set(rows.map(r => ({ lotNumber: r.lotNumber, suggestedQuantity: r.suggestedQuantity })));
        if (rows[0]) this.form.lotId = rows[0].lotId;
      }
    });
  }

  lotOptions(): OptionItem[] {
    const offeringId = this.fixedOfferingId ?? this.form.offeringId;
    const warehouseId = this.form.warehouseId || this.form.fromWarehouseId;
    return this.lots()
      .filter(lot =>
        (!offeringId || lot.offeringId === offeringId) &&
        (!warehouseId || lot.warehouseId === warehouseId))
      .map(lot => ({ label: lot.lotNumber, value: lot.id }));
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.error.set('');

    const afterLookups = () => {
      this.service.listInventoryMovements(undefined, 'Draft').subscribe({
        next: draftRows => this.drafts.set(draftRows.filter(m => this.matchesOffering(m)))
      });
      const history$ = this.fixedOfferingId
        ? this.service.listInventoryMovements(undefined, undefined, this.fixedOfferingId)
        : this.service.listInventoryMovements();
      history$.subscribe({
        next: movements => {
          this.movements.set(movements.filter(m => m.status !== 'Draft' && this.matchesOffering(m)));
          this.loading.set(false);
        },
        error: () => {
          this.error.set(this.t('catalog.movements.loadFailed'));
          this.loading.set(false);
        }
      });
    };

    const loadBinsAndContinue = () => {
      this.service.listWarehouseBins().subscribe({
        next: bins => {
          this.bins.set(bins.filter(item => item.isActive));
          afterLookups();
        },
        error: () => {
          this.error.set(this.t('catalog.movements.loadFailed'));
          this.loading.set(false);
        }
      });
    };

    const loadWarehouses = () => {
      this.service.listWarehouses().subscribe({
        next: warehouses => {
          this.warehouses.set(warehouses.filter(item => item.isActive));
          const firstWarehouseId = this.warehouses()[0]?.id ?? '';
          if (!this.form.warehouseId) this.form.warehouseId = firstWarehouseId;
          if (!this.form.fromWarehouseId) this.form.fromWarehouseId = firstWarehouseId;
          if (!this.form.toWarehouseId) this.form.toWarehouseId = firstWarehouseId;
          loadBinsAndContinue();
        },
        error: () => {
          this.error.set(this.t('catalog.movements.loadFailed'));
          this.loading.set(false);
        }
      });
    };

    if (this.fixedOfferingId) {
      this.form.offeringId = this.fixedOfferingId;
      this.service.getOffering(this.fixedOfferingId).subscribe({
        next: offering => this.offerings.set([offering]),
        error: () => this.offerings.set([])
      });
      loadWarehouses();
      return;
    }

    this.service.listOfferings().subscribe({
      next: offerings => {
        this.offerings.set(offerings.filter(item => item.inventoryEnabled));
        if (!this.form.offeringId && this.offerings().length > 0) {
          this.form.offeringId = this.offerings()[0].id;
        }
        loadWarehouses();
      },
      error: () => {
        this.error.set(this.t('catalog.movements.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  /** Opens the warehouse operation form modal (list-only tab UX). */
  openOperationDialog(): void {
    this.operationError.set('');
    this.generateIdempotencyKey();
    if (this.fixedOfferingId) {
      this.form.offeringId = this.fixedOfferingId;
    }
    this.refreshCostingDefaults();
    this.operationDialogVisible = true;
  }

  requiresUnitCost(): boolean {
    if (this.form.operationType === 'receipt') {
      return true;
    }
    return this.form.operationType === 'adjustment' && Number(this.form.quantity) > 0;
  }

  refreshCostingDefaults(): void {
    const offeringId = this.fixedOfferingId ?? this.form.offeringId;
    if (!offeringId) {
      return;
    }
    const warehouseId = this.isTransfer() ? undefined : (this.form.warehouseId || undefined);
    this.costingApi.listPolicies({ offeringId, warehouseId, isActive: true }).subscribe({
      next: policies => {
        const best = [...policies].sort((left, right) => right.specificityScore - left.specificityScore)[0];
        if (best?.currencyCode) {
          this.form.currencyCode = best.currencyCode;
        }
      }
    });
  }

  closeOperationDialog(): void {
    this.operationDialogVisible = false;
    this.operationError.set('');
  }

  submitOperation(): void {
    this.operationError.set('');
    this.error.set('');
    const offeringId = this.fixedOfferingId ?? this.form.offeringId;
    if (!offeringId || this.form.quantity <= 0 || !this.form.idempotencyKey.trim()) {
      this.operationError.set(this.t('catalog.movements.requiredFields'));
      return;
    }

    if (this.isTransfer()) {
      if (!this.form.fromWarehouseId || !this.form.toWarehouseId) {
        this.operationError.set(this.t('catalog.movements.requiredFields'));
        return;
      }
    } else if (!this.form.warehouseId) {
      this.operationError.set(this.t('catalog.movements.requiredFields'));
      return;
    }

    if (this.requiresUnitCost()) {
      const unitCost = Number(this.form.unitCost);
      if (!Number.isFinite(unitCost) || unitCost <= 0) {
        this.operationError.set(this.t('catalog.movements.unitCostRequired'));
        return;
      }
      if (!this.form.currencyCode?.trim()) {
        this.operationError.set(this.t('catalog.movements.currencyRequired'));
        return;
      }
    }

    const payload: InventoryMovementPayload = {
      offeringId,
      quantity: Number(this.form.quantity),
      warehouseId: this.isTransfer() ? null : this.form.warehouseId,
      binId: this.isTransfer() ? null : (this.form.binId || null),
      fromWarehouseId: this.isTransfer() ? this.form.fromWarehouseId : null,
      fromBinId: this.isTransfer() ? (this.form.fromBinId || null) : null,
      toWarehouseId: this.isTransfer() ? this.form.toWarehouseId : null,
      toBinId: this.isTransfer() ? (this.form.toBinId || null) : null,
      idempotencyKey: this.form.idempotencyKey.trim(),
      referenceNumber: this.form.referenceNumber.trim(),
      lotId: this.form.lotId || null,
      serialId: null,
      applyLotRotation: this.form.applyLotRotation,
      rotationPolicy: null,
      fefoOverrideReason: this.form.fefoOverrideReason.trim() || null,
      unitCost: this.requiresUnitCost() ? Number(this.form.unitCost) : null,
      currencyCode: this.requiresUnitCost() ? this.form.currencyCode.trim() : null
    };

    const submit$ = this.form.submitAsDraft
      ? this.service.submitInventoryMovementDraft(this.form.operationType, payload)
      : this.service.postInventoryMovement(this.form.operationType, payload);

    submit$.subscribe({
      next: movement => {
        this.generateIdempotencyKey();
        this.form.referenceNumber = '';
        this.closeOperationDialog();
        this.loadLots();
        this.load();
        this.inventoryChanged.emit();
        this.openDetail(movement.id);
      },
      error: err => this.operationError.set(this.resolveSubmitError(err))
    });
  }

  private resolveSubmitError(error: unknown): string {
    const code = (error as { error?: { error?: { code?: string } } })?.error?.error?.code;
    if (code === 'unit_cost_required') {
      return this.t('catalog.movements.unitCostRequired');
    }
    if (code === 'currency_code_required') {
      return this.t('catalog.movements.currencyRequired');
    }
    if (code === 'currency_code_mismatch') {
      return this.t('catalog.movements.currencyMismatch');
    }
    return this.t('catalog.movements.submitFailed');
  }

  openDetail(id: string): void {
    if (!this.fixedOfferingId) {
      void this.router.navigate(['/inventory/movements', id]);
      return;
    }
    this.detailDialogVisible = true;
    this.detailLoading.set(true);
    this.detailError.set('');
    this.detailMovement.set(null);
    this.service.getInventoryMovement(id).subscribe({
      next: movement => {
        this.detailMovement.set(movement);
        this.detailLoading.set(false);
      },
      error: () => {
        this.detailError.set(this.t('catalog.movements.notFound'));
        this.detailLoading.set(false);
      }
    });
  }

  closeDetail(): void {
    this.detailDialogVisible = false;
    this.detailMovement.set(null);
  }

  canRetryGl(movement: InventoryMovement): boolean {
    if (!movement.glPostingStatus) return false;
    return movement.glPostingStatus === 'Failed' || movement.glPostingStatus === 'Pending';
  }

  retryGl(id: string): void {
    this.retryingGl.set(true);
    this.service.retryInventoryMovementGlPosting(id).subscribe({
      next: movement => {
        this.detailMovement.set(movement);
        this.retryingGl.set(false);
        this.load(false);
        this.inventoryChanged.emit();
      },
      error: () => {
        this.detailError.set(this.t('catalog.costing.gl.retryFailed'));
        this.retryingGl.set(false);
      }
    });
  }

  printTransfer(id: string): void {
    this.costingApi.getTransferPrintDocument(id).subscribe({
      next: doc => {
        const body = [
          `${this.t('catalog.movements.transferPrintTitle')}: ${doc.transferReferenceNumber}`,
          `${this.t('catalog.movements.pairedIssueRef')}: ${doc.issueReferenceNumber}`,
          `${this.t('catalog.movements.pairedReceiptRef')}: ${doc.receiptReferenceNumber}`,
          `${this.t('catalog.inventory.quantity')}: ${doc.quantity}`
        ].join('\n');
        const w = window.open('', '_blank');
        if (w) {
          w.document.write(`<pre>${body}</pre>`);
          w.document.close();
          w.print();
        }
      },
      error: () => this.error.set(this.t('catalog.movements.printFailed'))
    });
  }

  isTransfer(): boolean {
    return this.form.operationType === 'transfer';
  }

  offeringOptions(): OptionItem[] {
    return this.offerings().map(item => ({
      label: `${item.code} - ${item.title}`,
      value: item.id
    }));
  }

  warehouseOptions(): OptionItem[] {
    return this.warehouses().map(item => ({
      label: `${item.code} - ${item.name}`,
      value: item.id
    }));
  }

  binOptions(warehouseId: string): OptionItem[] {
    if (!warehouseId) {
      return [];
    }
    return this.bins()
      .filter(item => item.warehouseId === warehouseId)
      .map(item => ({
        label: `${item.code} - ${item.name}`,
        value: item.id
      }));
  }

  resolveOfferingLabel(offeringId: string | null | undefined): string {
    if (!offeringId) {
      return '-';
    }
    const offering = this.offerings().find(item => item.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : offeringId;
  }

  resolveWarehouseLabel(warehouseId: string | null | undefined): string {
    if (!warehouseId) {
      return '-';
    }
    const warehouse = this.warehouses().find(item => item.id === warehouseId);
    return warehouse ? `${warehouse.code} - ${warehouse.name}` : warehouseId;
  }

  approveDraft(id: string): void {
    this.service.approveInventoryMovementDraft(id).subscribe({
      next: () => {
        this.load();
        this.inventoryChanged.emit();
      }
    });
  }

  generateIdempotencyKey(): void {
    this.form.idempotencyKey = crypto.randomUUID();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
